import { useEffect } from "react";
import { prefetchRoute, type PrefetchableRoute } from "@/lib/routePrefetch";

type NetworkInformationHint = {
  effectiveType?: string;
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationHint;
};

type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (id: number) => void;
};

const speculativeRoutesFor = (
  pathname: string,
): PrefetchableRoute[] => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/home") {
    return ["/profile"];
  }
  if (normalized === "/profile") return ["/meu-progresso"];
  if (normalized === "/meu-progresso") return ["/profile"];
  return [];
};

const allowsSpeculation = (): boolean => {
  const connection = (navigator as NavigatorWithConnection).connection;
  if (connection?.saveData) return false;
  return connection?.effectiveType !== "slow-2g" && connection?.effectiveType !== "2g";
};

export const scheduleIdlePrefetch = (task: () => void): (() => void) => {
  if (!allowsSpeculation()) return () => undefined;

  let cancelled = false;
  const run = () => {
    if (!cancelled) task();
  };
  const idleWindow = window as WindowWithIdleCallback;

  if (idleWindow.requestIdleCallback) {
    const idleId = idleWindow.requestIdleCallback(run, { timeout: 2000 });
    return () => {
      cancelled = true;
      idleWindow.cancelIdleCallback?.(idleId);
    };
  }

  const timer = window.setTimeout(run, 800);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
};

/**
 * Aquece somente as próximas rotas mais prováveis, depois da tela atual ficar
 * ociosa. Em economia de dados/2G, o prefetch continua disponível por intenção
 * explícita (pointer/foco), mas esta especulação automática é desligada.
 */
export const useIdleRoutePrefetch = (pathname: string, enabled = true): void => {
  useEffect(() => {
    const routes = speculativeRoutesFor(pathname);
    if (!enabled || routes.length === 0) return;

    return scheduleIdlePrefetch(() => {
      for (const route of routes) void prefetchRoute(route);
    });
  }, [enabled, pathname]);
};
